import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

// Inline SVG payment-method logos (Visa, Mastercard, Amex, Discover)
const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    {/* Visa */}
    <div className="h-7 w-12 bg-white rounded-md border border-gray-200 flex items-center justify-center px-1.5">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Visa">
        <text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text>
      </svg>
    </div>
    {/* Mastercard */}
    <div className="h-7 w-12 bg-white rounded-md border border-gray-200 flex items-center justify-center">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Mastercard">
        <circle cx="19" cy="16" r="9" fill="#EB001B" />
        <circle cx="29" cy="16" r="9" fill="#F79E1B" />
        <path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" />
      </svg>
    </div>
    {/* American Express */}
    <div className="h-7 w-12 bg-[#1F72CD] rounded-md flex items-center justify-center px-1">
      <svg viewBox="0 0 48 32" className="h-4 w-auto" aria-label="American Express">
        <text x="24" y="20" textAnchor="middle" fontSize="9" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif">AMEX</text>
      </svg>
    </div>
    {/* Discover */}
    <div className="h-7 w-12 bg-white rounded-md border border-gray-200 flex items-center justify-center px-1">
      <svg viewBox="0 0 60 32" className="h-4 w-auto" aria-label="Discover">
        <text x="2" y="20" fontSize="10" fontWeight="900" fill="#000000" fontFamily="Arial, sans-serif">DISC</text>
        <circle cx="50" cy="16" r="7" fill="#FF6000" />
      </svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 border-t border-gray-200 mt-12">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">

          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link to="/" className="inline-flex items-center mb-4">
              <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                EC <span className="text-amber-600">Tuning</span> Garage
              </span>
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed mb-5 max-w-sm">
              Musical instruments, tuning gear and studio audio &mdash; curated for passionate musicians and shipped fast from the USA.
            </p>
            <div className="space-y-2 text-sm">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2 text-gray-600 hover:text-amber-600">
                <Phone className="w-4 h-4 mt-0.5 text-amber-600" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2 text-gray-600 hover:text-amber-600">
                <Mail className="w-4 h-4 mt-0.5 text-amber-600" />
                <span className="break-all">{CONTACT_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2 text-gray-600">
                <MapPin className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                <span>
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                </span>
              </div>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-wider mb-4">Shop</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/shop" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">All Products</Link>
              </li>
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="text-gray-600 hover:text-amber-600 text-sm transition-colors">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-wider mb-4">Customer Care</h4>
            <ul className="space-y-2.5">
              {[
                { name: 'Shipping Info', path: '/shipping' },
                { name: 'Returns', path: '/returns' },
                { name: 'FAQ', path: '/faq' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms of Service', path: '/terms' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-gray-600 hover:text-amber-600 text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">About Us</Link></li>
              <li><Link to="/why-choose-us" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">Why Choose Us</Link></li>
              <li><Link to="/our-mission" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">Our Mission</Link></li>
              <li><Link to="/quality-guarantee" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">Quality Guarantee</Link></li>
              <li><Link to="/contact" className="text-gray-600 hover:text-amber-600 text-sm transition-colors">Contact Us</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-gray-600">
              <span>&copy; {currentYear} EC Tuning Garage</span>
              <span className="hidden sm:block text-gray-300">|</span>
              <span>{CONTACT_INFO.corpName}</span>
            </div>

            {/* Center - Payment Logos */}
            <div className="flex items-center gap-3">
              <span className="text-gray-500 text-xs font-medium">We Accept</span>
              <PaymentLogos />
            </div>

            {/* Right */}
            <div className="text-xs text-gray-500">
              Charges appear as &quot;<span className="font-semibold text-gray-700">{CARD_DESCRIPTOR}</span>&quot;
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
